/**
 * 정비 모드 PIN 을 새로 정하는 입력.
 *
 * **정해 둔 PIN 은 다시 볼 수 없다** — 서버에도 해시만 있다. 그래서 오타를 나중에 확인할
 * 길이 없어 두 번 받는다. 잘못 정한 PIN 은 현장 기술자가 화면 앞에서 처음 알게 되고,
 * 그때는 콘솔에서 다시 정하기 전까지 그 화면의 정비 모드가 닫혀 있다.
 *
 * 화면 상세(한 대)와 화면 목록의 일괄 적용(여러 대)이 함께 쓴다.
 */
import { useState, type ReactNode } from 'react';
import { HelpTip } from './HelpTip.js';

/** 서버 규칙(`services/maintenance-pin.ts`)과 같다. 여기서는 단추를 막을 뿐, 최종 관문은 서버다. */
const PIN_RULE = /^[0-9]{4,8}$/;

interface Props {
  pending: boolean;
  /** 되돌리기 단추를 보일지. 콘솔 PIN 이 없는 한 대에는 되돌릴 것이 없다. */
  canClear: boolean;
  onSave: (pin: string) => void;
  onClear: () => void;
}

export function MaintenancePinForm({ pending, canClear, onSave, onClear }: Props): ReactNode {
  const [pin, setPin] = useState('');
  const [again, setAgain] = useState('');

  const valid = PIN_RULE.test(pin);
  const mismatch = again.length > 0 && again !== pin;
  const ready = valid && again === pin && !pending;

  // 숫자판으로 칠 수 있는 것만 남긴다. 화면에는 숫자 말고 누를 것이 없다.
  const digits = (value: string): string => value.replace(/\D/g, '').slice(0, 8);

  return (
    <div className="flex flex-col gap-3">
      <label className="field">
        <span className="field-label">
          새 PIN
          <HelpTip
            text="정한 PIN 은 다시 볼 수 없습니다. 30초 안에 적용되며(오프라인 화면은 다시 연결될 때), 콘솔 PIN 을 해제하면 기기 PIN(기본 0000)을 씁니다."
            label="정비 모드 PIN 도움말"
          />
        </span>
        <input
          className="input w-full"
          type="password"
          inputMode="numeric"
          autoComplete="new-password"
          placeholder="숫자 4~8자리"
          value={pin}
          disabled={pending}
          onChange={(event) => setPin(digits(event.target.value))}
        />
      </label>
      <label className="field">
        <span className="field-label">PIN 확인</span>
        <input
          className="input w-full"
          type="password"
          inputMode="numeric"
          autoComplete="new-password"
          value={again}
          disabled={pending}
          onChange={(event) => setAgain(digits(event.target.value))}
        />
        {mismatch ? <span className="hint text-red-600">PIN 이 일치하지 않습니다.</span> : null}
      </label>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="btn btn-primary"
          disabled={!ready}
          onClick={() => {
            onSave(pin);
            setPin('');
            setAgain('');
          }}
        >
          {pending ? '저장 중…' : 'PIN 저장'}
        </button>
        {canClear ? (
          <button type="button" className="btn" disabled={pending} onClick={onClear}>
            콘솔 PIN 해제
          </button>
        ) : null}
      </div>
    </div>
  );
}
